import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function AcceptOwnership() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const token = params.get("token");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");
  const [brandName, setBrandName] = useState("");

  // Preserve the invite link across the login round-trip.
  useEffect(() => {
    if (token) localStorage.setItem("bh_pending_transfer", token);
  }, [token]);

  // Fetch the brand name so the recipient sees which workspace they're accepting.
  useEffect(() => {
    if (!token || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/brands/transfer-ownership/preview", { token });
        if (!cancelled) setBrandName(data.brand_name || "");
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || t("accept.failed"));
      }
    })();
    return () => { cancelled = true; };
  }, [token, user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <CenterShell><Spinner /> {t("accept.checking")}</CenterShell>;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!token) {
    return <CenterShell>{t("accept.failed")}</CenterShell>;
  }

  const accept = async () => {
    setStatus("sending");
    setError("");
    try {
      await api.post("/brands/transfer-ownership/accept", { token });
      localStorage.removeItem("bh_pending_transfer");
      setStatus("done");
      toast.success(t("accept.success"));
      // Give the user a moment to see the success state before redirect.
      setTimeout(() => navigate("/", { replace: true }), 900);
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail || t("accept.failed"));
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f3f1] flex items-center justify-center p-6">
      <div
        data-testid="accept-ownership-card"
        className="w-full max-w-md bg-white rounded-2xl p-8 shadow-sm"
      >
        <h1 className="font-display text-3xl font-medium text-[#1a1a1a] mb-2">
          {t("accept.title")}
        </h1>
        <p className="text-sm text-gray-500 mb-8">
          {t("accept.description").replace("{brand}", brandName || t("accept.this_workspace"))}
        </p>

        {status === "done" ? (
          <div className="flex items-center gap-2 text-[#1a1a1a]">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-medium">{t("accept.success")}</span>
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <Button
              data-testid="accept-ownership-confirm"
              onClick={accept}
              disabled={status === "sending"}
              className="w-full h-11 rounded-lg bg-[#1a1a1a] text-white hover:bg-black/90 disabled:opacity-60"
            >
              {status === "sending" ? t("accept.accepting") : t("accept.accept_button")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function CenterShell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center gap-2 text-[#1a1a1a]">
      {children}
    </div>
  );
}

function Spinner() {
  return <Loader2 className="w-4 h-4 animate-spin" />;
}
